import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import {
  useCrews,
  useJobs,
  useTeamMembers,
  useTerritories,
} from "@/hooks/use-api";
import { useJobsFilters } from "@/hooks/use-jobs-filters";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { EmptyState } from "@/components/v2/empty-state";
import { ViewSwitcher } from "@/components/v2/view-switcher";
import { AggregateStrip } from "@/components/v2/aggregate-strip";
import { FilterToolbar } from "@/components/v2/filter-toolbar";
import { MultiSelectFilter } from "@/components/v2/multi-select-filter";
import { SearchHint } from "@/components/v2/search-hint";
import { JobsCalendar } from "@/components/v2/calendar/jobs-calendar";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatDate } from "@/lib/utils";
import { LIST_PAGE_VIEWS } from "@/pages/v2/_shared/list-page-views";
import { formatShowingRange } from "@/pages/v2/_shared/format-showing-range";
import { useAddDemoAccountStore } from "@/stores/add-demo-account-store";

type Row = Record<string, unknown>;

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "job_number",
    header: "Job #",
    cell: ({ row }) => {
      const name = row.original.name as string | undefined;
      const jobNumber = row.original.job_number as string | undefined;
      return (
        <span className="typo-mono font-medium">
          {jobNumber || name || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "customer_name",
    header: "Customer",
    cell: ({ row }) => {
      const name = row.original.customer_name as string | undefined;
      return (
        <span className={name ? "" : "text-[color:var(--color-text-lo)]"}>
          {name || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "crew_name",
    header: "Crew",
    enableSorting: false,
    cell: ({ row }) => {
      const crew = row.original.crew_name as string | undefined;
      return (
        <span className={crew ? "" : "text-[color:var(--color-text-lo)]"}>
          {crew || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "assigned_rep_name",
    header: "Rep",
    cell: ({ row }) => {
      const rep = row.original.assigned_rep_name as string | undefined;
      return (
        <span className={rep ? "" : "text-[color:var(--color-text-lo)]"}>
          {rep || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status as string} />,
  },
  {
    accessorKey: "scheduled_date",
    header: "Scheduled",
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.scheduled_date as string)}
      </span>
    ),
  },
  {
    accessorKey: "total_revenue",
    header: "Amount",
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="font-medium">
        {row.original.total_revenue != null
          ? formatCurrency(row.original.total_revenue as number)
          : "—"}
      </span>
    ),
  },
];

const VIEWS = LIST_PAGE_VIEWS.jobs;

// team-members rows are TeamMemberUnified — keyed by user_id (the column the
// jobs endpoint filters on via salesperson_user_id), NOT a row `id`. user_id is
// nullable, so rows without one are excluded from the rep filter below.
type TeamMemberRow = {
  user_id: string | null;
  first_name?: string;
  last_name?: string;
};
type CrewRow = { id: string; name?: string };
type TerritoryRow = { id: string; name?: string };

export default function V2JobsPage() {
  const navigate = useNavigate();
  const { openDialog } = useAddDemoAccountStore();
  const [view, setView] = useState<string>(VIEWS.defaultValue);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "scheduled_date", desc: true },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const filters = useJobsFilters();

  const { data, isLoading, isError, refetch } = useJobs({
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
    ...filters.params,
  });

  const { data: crewsData } = useCrews({ limit: 100 });
  const { data: teamData } = useTeamMembers({ limit: 100 });
  const { data: territoriesData } = useTerritories();

  const crewOptions = ((crewsData?.crews ?? []) as CrewRow[]).map((c) => ({
    id: c.id,
    name: c.name ?? c.id,
  }));
  const salespersonOptions = ((teamData?.team_members ?? []) as TeamMemberRow[])
    .filter((m): m is TeamMemberRow & { user_id: string } => m.user_id != null)
    .map((m) => ({
      id: m.user_id,
      name: [m.first_name, m.last_name].filter(Boolean).join(" ") || m.user_id,
    }));
  const territoryOptions = (
    (territoriesData?.territories ?? []) as TerritoryRow[]
  ).map((t) => ({ id: t.id, name: t.name ?? t.id }));

  const jobs = (data?.jobs ?? []) as Row[];
  const total = data?.total ?? 0;

  // TODO(ADV-787-followup): Revenue / Scheduled / In progress cells need a
  // DB-side ops.v_jobs_aggregate view (analogous to the Opportunities one).
  // Until that lands we render `—` rather than .reduce() across the page.
  const aggregateItems = [
    {
      label: "Showing",
      value: formatShowingRange(jobs.length, pageIndex, pageSize, total),
    },
    { label: "Revenue", value: "—" },
    { label: "Scheduled", value: "—" },
    { label: "In progress", value: "—" },
  ];

  return (
    <ListPage
      title="Jobs"
      actions={
        <>
          <SearchHint />
          <Button onClick={() => navigate("/jobs/new")}>Schedule job</Button>
        </>
      }
      tabs={
        <ViewSwitcher
          value={view}
          onValueChange={setView}
          options={[...VIEWS.options]}
          aria-label="Jobs view"
        />
      }
      filters={
        <FilterToolbar entity="jobs">
          <MultiSelectFilter
            label="Crew"
            options={crewOptions}
            selected={filters.crewIds}
            onChange={filters.setCrewIds}
          />
          <MultiSelectFilter
            label="Rep"
            options={salespersonOptions}
            selected={filters.salespersonIds}
            onChange={filters.setSalespersonIds}
          />
          <MultiSelectFilter
            label="Territory"
            options={territoryOptions}
            selected={filters.territoryIds}
            onChange={filters.setTerritoryIds}
          />
        </FilterToolbar>
      }
      aggregateStrip={
        view === "table" ? (
          <AggregateStrip
            items={aggregateItems}
            sort={{ label: "Scheduled", direction: "desc" }}
          />
        ) : undefined
      }
    >
      {view === "calendar" && <JobsCalendar extraParams={filters.params} />}
      {view === "table" && (
        <DataTable
          columns={columns}
          data={jobs}
          total={total}
          isLoading={isLoading}
          isError={isError}
          errorEntityLabel="jobs"
          onRetry={() => refetch()}
          onRowClick={(row) => navigate(`/jobs/${row.id}`)}
          emptyMessage="No scheduled jobs"
          emptyDescription="Jobs appear here once a proposal is accepted and scheduled."
          pageSize={pageSize}
          pageIndex={pageIndex}
          onPaginationChange={setPageIndex}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      )}
      {view === "board" && (
        <EmptyState
          title="Board view ships in ADV-Board"
          description="The Jobs kanban lands in a follow-up ticket."
          action={{ label: "View as table", onClick: () => setView("table") }}
          secondaryAction={{
            label: "Try Adva with sample data",
            onClick: openDialog,
          }}
        />
      )}
      {view === "analytics" && (
        <EmptyState
          title="Analytics ships in Wave 2"
          description="The Jobs analytics surface is part of the broader operational-screens plan."
          secondaryAction={{
            label: "Try Adva with sample data",
            onClick: openDialog,
          }}
        />
      )}
    </ListPage>
  );
}
